import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Stack,
    Typography
} from '@mui/material';
import {
    cloneHtmlTemplate,
    deleteHtmlTemplate,
    getHtmlTemplates
} from '../Api';
import {Link, useNavigate} from 'react-router-dom';
import HtmlTemplateDto from '../models/dtos/HtmlTemplateDto';
import HtmlTemplatePreview from '../components/HtmlTemplatePreview';
import AndroidIcon from '@mui/icons-material/Android';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SettingsIcon from '@mui/icons-material/Settings';

/** AI hub tasks of analytics' HTML template AI (edited in AI › Tasks). */
const IMPROVE_TASK_CODE = 'offers.improve-html-template';
const LOCALIZE_TASK_CODE = 'offers.localize-html-template';

export default function HtmlTemplatesPage() {
    const [state, setState] = React.useState({
        templatesLoaded: false,
        templates: [] as HtmlTemplateDto[],
        cloningTemplateId: null as string | null,
        deleteDialogOpen: false,
        deleteTargetId: null as string | null,
        deleteTargetName: '',
        errorDialogOpen: false,
        errorDialogMsg: ''
    });

    const navigate = useNavigate();

    React.useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const templates = await getHtmlTemplates();
        setState(prevState => ({
            ...prevState,
            templates,
            templatesLoaded: true
        }));
    };

    const openDeleteDialog = (id: string, name: string) => {
        setState(prev => ({...prev, deleteDialogOpen: true, deleteTargetId: id, deleteTargetName: name}));
    };

    const handleDeleteConfirm = async () => {
        if (state.deleteTargetId) {
            const result = await deleteHtmlTemplate(state.deleteTargetId);
            if (result) {
                setState(prev => ({
                    ...prev,
                    templates: prev.templates.filter(t => t.id !== prev.deleteTargetId),
                    deleteDialogOpen: false,
                    deleteTargetId: null,
                    deleteTargetName: ''
                }));
            }
        }
    };

    const handleDeleteCancel = () => {
        setState(prev => ({...prev, deleteDialogOpen: false, deleteTargetId: null, deleteTargetName: ''}));
    };

    const handleCloneTemplate = async (id: string) => {
        setState(prev => ({...prev, cloningTemplateId: id}));
        const result = await cloneHtmlTemplate(id);

        if (result?.id) {
            navigate('/funnels/html-templates/' + result.id + '/edit');
            return;
        }

        setState(prev => ({
            ...prev,
            cloningTemplateId: null,
            errorDialogOpen: true,
            errorDialogMsg: 'Failed to clone template'
        }));
    };

    return (
        <Stack gap="50px 50px" sx={{mx: 5, my: 5}} justifyContent='center' alignItems='center'>
            <Grid container spacing={2} columns={{xs: 1, sm: 2, md: 4, lg: 5}} justifyContent="flex-end">
                <Grid item xs={1} key="addButton" alignContent="center">
                    <Button variant="contained" fullWidth={true} style={{height: '54px'}}
                            onClick={() => navigate('/funnels/html-templates/create')}>
                        Add HTML template
                    </Button>
                </Grid>
                <Grid item xs={1} key="funnelsButton" alignContent="center">
                    <Button variant="outlined" fullWidth={true} style={{height: '54px'}}
                            onClick={() => navigate('/funnels')}>
                        Go to funnels
                    </Button>
                </Grid>
            </Grid>
            <Typography variant="h3">HTML Templates</Typography>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: -3}}>
                <SettingsIcon fontSize="small" color="action"/>
                <Typography variant="body2" color="text.secondary">
                    AI prompts and model (AI hub):{' '}
                    <Link to={'/ai/tasks/' + IMPROVE_TASK_CODE}>improvements</Link>
                    {' · '}
                    <Link to={'/ai/tasks/' + LOCALIZE_TASK_CODE}>localizations</Link>
                </Typography>
            </Box>
            {!state.templatesLoaded ? (
                <Grid item xs={2}>
                    <CircularProgress size='25%' style={{marginBottom: '32px', marginLeft: '35%'}}/>
                </Grid>
            ) : <div/>}
            <Grid container spacing={5} columns={{xs: 1, sm: 1, md: 2, lg: 3, xl: 4}}>
                <Box width='100%'/>
                {state.templates.map(template => (
                    <Grid item xs={1} key={template.id}>
                        <Card sx={{width: '100%'}}>
                            <CardContent>
                                <Typography gutterBottom variant="h5" component="div">
                                    {template.name}
                                </Typography>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                        {template.osType === 'ios'
                                            ? <PhoneIphoneIcon sx={{fontSize: 28, color: 'text.secondary'}}/>
                                            : <AndroidIcon sx={{fontSize: 28, color: '#3ddc84'}}/>
                                        }
                                        <Typography variant="body1" fontWeight={500}>
                                            {template.osType === 'ios' ? 'iOS' : 'Android'}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={template.isValid ? 'Valid' : 'Invalid'}
                                        size="small"
                                        color={template.isValid ? 'success' : 'error'}
                                        variant="filled"
                                    />
                                </Box>
                                <Box sx={{mb: 1.5}}>
                                    <HtmlTemplatePreview
                                        htmlContent={template.htmlContent}
                                        localizations={template.localizations}
                                        betcoinsMacrosKey={template.betcoinsMacrosKey}
                                        expirationTimestampMacrosKey={template.expirationTimestampMacrosKey}
                                        discountMacroKey={template.discountMacroKey}
                                        oldPriceKeys={template.oldPriceKeys}
                                        newPriceKeys={template.newPriceKeys}
                                        width="100%"
                                        borderRadius="12px"
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                    ID: {template.id}
                                </Typography>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                                    {template.localizations.map((loc, idx) => (
                                        <Chip
                                            key={idx}
                                            label={loc.id || `Key ${idx + 1}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </CardContent>
                            <CardActions>
                                <Button size="small" color="error" variant="outlined"
                                        onClick={() => openDeleteDialog(template.id, template.name)}>
                                    Delete
                                </Button>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    disabled={state.cloningTemplateId != null}
                                    onClick={() => handleCloneTemplate(template.id)}>
                                    {state.cloningTemplateId === template.id ? 'Cloning...' : 'Clone'}
                                </Button>
                                <Button size="small" color="primary" variant="outlined" sx={{marginLeft: 'auto'}}
                                        onClick={() => navigate('/funnels/html-templates/' + template.id + '/edit')}>
                                    Edit
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog
                open={state.deleteDialogOpen}
                onClose={handleDeleteCancel}>
                <DialogTitle>Delete HTML Template</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the template "{state.deleteTargetName}"?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={state.errorDialogOpen}
                onClose={() => setState(prev => ({...prev, errorDialogOpen: false, errorDialogMsg: '', cloningTemplateId: null}))}>
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <DialogContentText>{state.errorDialogMsg}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setState(prev => ({...prev, errorDialogOpen: false, errorDialogMsg: '', cloningTemplateId: null}))}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
